import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipFrame, clipOutroFade, combineSceneFragments, makeTl, typeSize, weights, withAlpha,
  colors as themeColors, escapeHtml,
} from "../../reel/sceneShared.js";

export const typewriter: SceneBuilder = ({ ctx, clipPrefix, inputs, clipDurationSec, clipStartSec }) => {
  const text = inputs.text ?? "Schreibe deinen Text…";
  const subtext = inputs.subtext ?? "";
  const variant = inputs.variant ?? "code";
  const accent = accentFor(inputs);
  const isCode = variant === "code";
  const cps = variant === "fast" ? 38 : isCode ? 26 : 18;
  const typeDurSec = text.length / cps;

  const lg = typeSize("lg", ctx.format);
  const md = typeSize("md", ctx.format);

  const t = makeTl(clipStartSec);
  const txtId = `${clipPrefix}-txt`;
  const caretId = `${clipPrefix}-cv`;
  const subId = `${clipPrefix}-sub`;

  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: isCode ? "studio" : "cinematic", accent });

  const codeFontSize = ctx.format === "9:16" ? 44 : 38;
  const headFontSize = lg;

  const textBlock = isCode
    ? `<div style="max-width:${ctx.format === "9:16" ? 900 : 1500}px;padding:40px 64px;border-radius:18px;background:${withAlpha("#000000", 0.55)};border:1px solid ${withAlpha(themeColors.fg, 0.1)};box-shadow:0 24px 80px ${withAlpha("#000000", 0.5)};backdrop-filter:blur(6px);">
  <div style="display:flex;gap:8px;margin-bottom:24px;opacity:.6;">
    <span style="width:12px;height:12px;border-radius:99px;background:#ff5f57;"></span><span style="width:12px;height:12px;border-radius:99px;background:#febc2e;"></span><span style="width:12px;height:12px;border-radius:99px;background:#28c840;"></span>
  </div>
  <div style="font-family:ui-monospace,'SF Mono',Menlo,monospace;font-weight:${weights.semibold};font-size:${codeFontSize}px;color:${themeColors.fg};line-height:1.3;white-space:pre-wrap;">
    <span style="color:${accent};">${"&gt; "}</span><span id="${txtId}"></span><span id="${caretId}" style="display:inline-block;width:.55em;height:1em;margin-left:4px;vertical-align:-.12em;background:${accent};box-shadow:0 0 16px ${withAlpha(accent, 0.7)};"></span>
  </div>
</div>`
    : `<h1 style="margin:0;font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${headFontSize}px;line-height:.95;letter-spacing:-2px;color:${themeColors.fg};text-align:center;text-wrap:balance;max-width:${ctx.format === "9:16" ? 920 : 1500}px;"><span id="${txtId}"></span><span id="${caretId}" style="display:inline-block;width:.06em;height:.9em;margin-left:6px;vertical-align:-.08em;background:${accent};box-shadow:0 0 24px ${withAlpha(accent, 0.7)};"></span></h1>`;

  const inner = `${textBlock}
${subtext ? `<div id="${subId}" style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${md * 0.55}px;color:${withAlpha(themeColors.fg, 0.85)};text-align:${isCode ? "left" : "center"};max-width:${ctx.format === "9:16" ? 760 : 900}px;letter-spacing:-0.4px;">${escapeHtml(subtext)}</div>` : ""}`;

  const html = clipFrame({
    clipPrefix, clipStartSec, clipDurationSec, inner, gapPx: 40,
    align: isCode ? "start" : "center",
  });

  const safeText = JSON.stringify(text);
  const typeStart = 6 / 30;
  const tweens: string[] = [
    `tl.to({v:0},{v:${text.length},duration:${typeDurSec},ease:"power1.inOut",onUpdate:function(){var v=Math.round(this.targets()[0].v);var el=document.getElementById(${JSON.stringify(txtId)});if(el)el.textContent=${safeText}.slice(0,v);}},${(clipStartSec + typeStart).toFixed(4)});`,
    `tl.to(${JSON.stringify("#" + caretId)},{opacity:0,duration:0.001,repeat:Math.max(1,Math.floor(${clipDurationSec * 4})),yoyo:true,repeatDelay:0.25,ease:"none"},${clipStartSec.toFixed(4)});`,
  ];
  if (subtext) tweens.push(t.to(`#${subId}`, { opacity: 1, y: 0, duration: 0.5, ease: "raw:\"power2.out\"" }, typeStart + typeDurSec + 0.1));
  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec));

  const init = subtext ? `gsap.set("#${subId}", { opacity: 0, y: 14 });` : "";

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
